import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { AuthorCard } from './AuthorCard';
import { ShareBar } from './ShareBar';
import { PeopleProfileData } from './PeopleProfileDialog';

export interface ArticleHeaderProps {
  /** Article title */
  title: string;
  /** Optional subtitle/deck */
  subtitle?: string;
  /** Topic name for badge */
  topicName?: string;
  /** CMS-managed topic color (hex) for badge background */
  topicColor?: string;
  /** Published date (ISO string) */
  publishedAt: string;
  /** Estimated read time string */
  readTime: string;
  /** Optional author profile data - when provided, renders clickable AuthorCard */
  authorProfile?: PeopleProfileData;
  /** Back navigation link */
  backLink?: {
    href: string;
    label: string;
  };
  /** Article URL for sharing */
  shareUrl?: string;
}

/**
 * component-article-header
 * 
 * Complete article page header lockup following the atomic design token system.
 * 
 * Token Composition:
 * - Back Link: atom-text-article-back-link + Lucide ArrowLeft
 * - Topic Badge: atom-tag-topic (dynamic bg from topicColor prop)
 * - Title: atom-text-article-headline
 * - Subtitle: atom-text-article-subtitle
 * - Meta Row: atom-text-article-meta + Lucide Calendar + Clock icons
 * - Author (optional): molecule-author-detailed via AuthorCard (clickable opens profile dialog)
 */
export function ArticleHeader({
  title,
  subtitle,
  topicName,
  topicColor,
  publishedAt,
  readTime,
  authorProfile,
  backLink = { href: '/learn', label: 'Back to Learn' },
  shareUrl,
}: ArticleHeaderProps) {
  // Format the date
  const formattedDate = format(new Date(publishedAt), 'MMMM d, yyyy');

  return (
    <header>
      {/* atom-text-article-back-link: Back navigation */}
      {backLink && (
        <Link
          to={backLink.href}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLink.label}
        </Link>
      )}

      {/* atom-tag-topic: Topic badge with CMS-managed color */}
      {topicName && (
        <div className="mb-4">
          <span
            className="inline-block text-xs font-semibold px-3 py-1 rounded-full text-white"
            style={{ backgroundColor: topicColor || 'hsl(var(--primary))' }}
          >
            {topicName}
          </span>
        </div>
      )}

      {/* primitive-type-display-56: Main title */}
      <h1 className="text-40 md:text-56 font-bold tracking-tight text-foreground mb-4">
        {title}
      </h1>

      {/* atom-text-article-subtitle: Optional subtitle - larger than body text */}
      {subtitle && (
        <p className="text-25 text-muted-foreground mb-6">
          {subtitle}
        </p>
      )}

      {/* atom-text-article-meta: Date, read time, author, and share */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-4 w-4" />
          <time dateTime={publishedAt}>{formattedDate}</time>
        </div>
        <span className="text-muted-foreground">·</span>
        <div className="flex items-center gap-1.5">
          <Clock className="h-4 w-4" />
          <span>{readTime}</span>
        </div>
        {/* molecule-author-inline: Compact author byline */}
        {authorProfile && (
          <>
            <span className="text-muted-foreground">·</span>
            <AuthorCard
              name={authorProfile.name}
              avatarUrl={authorProfile.image_url || undefined}
              variant="inline"
              profile={authorProfile}
            />
          </>
        )}
        {/* molecule-share-bar: Social sharing buttons inline */}
        {shareUrl && (
          <>
            <span className="text-muted-foreground">·</span>
            <ShareBar url={shareUrl} title={title} variant="compact" />
          </>
        )}
      </div>

      {/* Divider before content */}
      <div className="mt-6 pt-6 border-t border-border" />
    </header>
  );
}

export default ArticleHeader;
